import { PublicOnly, RequireAuth } from '@iris/ui/auth/guards';
import { createBrowserRouter } from 'react-router-dom';

import { Layout } from './layout';
import { RouteError } from './route-error';

/** Routes are lazy-loaded chunks (T4 §1.2); each screen arrives with its milestone. */
export const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      {
        element: <PublicOnly />,
        children: [
          {
            path: 'login',
            lazy: async () => {
              const { LoginScreen } = await import('@iris/ui/auth/login-screen');
              return {
                Component: () => (
                  <LoginScreen
                    titleKey="auth.login.merchantTitle"
                    signupHref="/signup"
                    forgotHref="/forgot-password"
                  />
                ),
              };
            },
          },
          {
            // owners create their account here, then their shop (US-O01); no first-name screen on this app
            path: 'signup',
            lazy: async () => {
              const { SignupScreen } = await import('@iris/ui/auth/signup-screen');
              return { Component: () => <SignupScreen welcomeHref={null} /> };
            },
          },
          {
            path: 'forgot-password',
            lazy: async () => ({
              Component: (await import('@iris/ui/auth/forgot-password-screen')).ForgotPasswordScreen,
            }),
          },
        ],
      },
      {
        path: 'reset-password',
        lazy: async () => ({
          Component: (await import('@iris/ui/auth/reset-password-screen')).ResetPasswordScreen,
        }),
      },
      {
        element: <RequireAuth />,
        children: [
          { index: true, lazy: () => import('./routes/home') },
          { path: 'onboarding/:step?', lazy: () => import('./routes/onboarding') },
          { path: 'account', lazy: () => import('./routes/account') },
          { path: 'scan', lazy: () => import('./routes/scan') },
          { path: 'scan/code', lazy: () => import('./routes/scan-code') },
          { path: 'scan/voucher-code', lazy: () => import('./routes/scan-voucher-code') },
          { path: 'today', lazy: () => import('./routes/today') },
          { path: 'staff', lazy: () => import('./routes/staff') },
          { path: 'programs', lazy: () => import('./routes/programs') },
          { path: 'programs/new', lazy: () => import('./routes/program-new') },
          { path: 'programs/:id', lazy: () => import('./routes/program') },
          { path: 'rewards', lazy: () => import('./routes/rewards') },
          { path: 'rewards/:id', lazy: () => import('./routes/reward') },
          { path: 'dashboard', lazy: () => import('./routes/dashboard') },
          { path: 'dashboard/staff/:userId', lazy: () => import('./routes/staff-visits') },
          { path: 'alerts', lazy: () => import('./routes/alerts') },
          { path: 'shop', lazy: () => import('./routes/shop') },
          { path: 'shop/qr-kit', lazy: () => import('./routes/qr-kit') },
          { path: 'billing', lazy: () => import('./routes/billing') },
          { path: 'settings', lazy: () => import('./routes/settings') },
          { path: 'more', lazy: () => import('./routes/more') },
          { path: 'promotions', lazy: () => import('./routes/promotions') },
          { path: 'promotions/new', lazy: () => import('./routes/promotion-new') },
          { path: 'promotions/:id', lazy: () => import('./routes/promotion') },
          // the invite link needs a session: RequireAuth sends to /login (or /signup) and back here
          { path: 'invite/:token', lazy: () => import('./routes/invite') },
        ],
      },
      { path: '*', lazy: () => import('./routes/not-found') },
    ],
  },
]);
